import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import type { AnswerRequest, Form } from "../dynamicForm";
import { api } from "./apiClient";
import type {
  PersonalDataControllersGridView,
  PersonalDataControllerById,
  CreatePersonalDataControllerPayload,
  CreatedPersonalDataController,
} from "./personalDataControllers.types";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";
import { prefetch } from "./prefetch";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/pdc`;

const personalDataControllersKeys = {
  all: () => ["personalDataControllers"],
  byId: (id: string) => [...personalDataControllersKeys.all(), id] as const,
  gridView: () => [...personalDataControllersKeys.all(), "gridview"] as const,
  form: () => [...personalDataControllersKeys.all(), "form"] as const,
  list: () => [...personalDataControllersKeys.all(), "list"] as const,
  displayList: () => [...personalDataControllersKeys.all(), "displayList"] as const,
};

/** Create personal data controller */
export const useCreatePersonalDataController = () => {
  const queryClient = useQueryClient();
  const createPersonalDataController = (payload: CreatePersonalDataControllerPayload) =>
    api.post(BASE_ENDPOINT, payload) as Promise<CreatedPersonalDataController>;

  return useMutation({
    mutationFn: createPersonalDataController,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Get personal data controllers form */
const personalDataControllerFormQueryOptions = () => {
  return queryOptions({
    queryKey: personalDataControllersKeys.form(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/form`) as Promise<Form>,
    throwOnError: true,
  });
};
export const useGetPersonalDataControllerForm = () => useQuery(personalDataControllerFormQueryOptions());
export const prefetchPersonalDataControllerForm = () => prefetch(personalDataControllerFormQueryOptions());
/** Get personal data controller by id */
const personalDataControllerByIdQueryOptions = (id: string) => {
  return queryOptions({
    queryKey: personalDataControllersKeys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}`) as Promise<PersonalDataControllerById>,
    throwOnError: true,
  });
};
export const useGetPersonalDataControllerById = (id: string) => useQuery(personalDataControllerByIdQueryOptions(id));
export const prefetchPersonalDataControllerById = (id: string) => prefetch(personalDataControllerByIdQueryOptions(id));

/** Get personal data controllers grid view */
const personalDataControllersGridViewQueryOptions = () =>
  queryOptions({
    queryKey: personalDataControllersKeys.gridView(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/gridview`) as Promise<PersonalDataControllersGridView>,
    throwOnError: true,
  });

export const useGetPersonalDataControllersGridView = () => useQuery(personalDataControllersGridViewQueryOptions());
export const prefetchPersonalDataControllersGridView = () => prefetch(personalDataControllersGridViewQueryOptions());

/** Update personal data controller answers */
export const useUpdatePersonalDataControllerAnswers = () => {
  const queryClient = useQueryClient();
  const updatePersonalDataControllerAnswers = ({
    personalDataControllerId,
    answers,
  }: {
    personalDataControllerId: string;
    answers: AnswerRequest[];
  }) => api.patch(`${BASE_ENDPOINT}/${personalDataControllerId}/answers`, answers);

  return useMutation({
    mutationFn: updatePersonalDataControllerAnswers,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Delete personal data controller */
export const useDeletePersonalDataController = () => {
  const deletePersonalDataController = (personalDataControllerId: string) =>
    api.delete(`${BASE_ENDPOINT}/${personalDataControllerId}`);

  return useMutation({
    mutationFn: deletePersonalDataController,
    onSuccess: (_, id) => invalidateAll({ ignore: [personalDataControllersKeys.byId(id)] }),
  });
};

const PersonalDataControllerListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type PersonalDataControllerListItem = z.infer<typeof PersonalDataControllerListItemSchema>;
export const personalDataControllersListQueryOptions = () =>
  queryOptions({
    queryKey: personalDataControllersKeys.list(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/list`, { schema: z.array(PersonalDataControllerListItemSchema) }),
    throwOnError: true,
  });

export const personalDataControllersDisplayListQueryOptions = () =>
  queryOptions({
    queryKey: personalDataControllersKeys.displayList(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/display-list`, { schema: z.array(PersonalDataControllerListItemSchema) }),
    throwOnError: true,
  });

export const useGetPersonalDataControllersGridViewFromCache = () => {
  const queryClient = useQueryClient();
  return queryClient.getQueryData(personalDataControllersKeys.gridView()) as PersonalDataControllersGridView;
};
